import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import "./CreateOrganization.css";
import logoFM from "../../assets/logo_trucktronix.png";
import {
  serviceGetOrganizationDetails,
  serviceRegisterOrganization,
} from "../../api/serviceFleetMaster";

const CreateOrganization = () => {
  const navigate = useNavigate();

  const [orgName, setOrgName] = useState("");
  const [orgCode, setOrgCode] = useState("");
  const [orgAddress, setOrgAddress] = useState("");
  const [loading, setLoading] = useState(false);

  const canSubmit = useMemo(() => {
    if (loading) return false;
    if (!orgName.trim()) return false;
    if (orgCode.trim().length !== 4) return false;
    return true;
  }, [orgName, orgCode, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) return;

    setLoading(true);

    try {
      const payload = {
        name: orgName.trim(),
        code: orgCode.trim().toUpperCase(),
        address: orgAddress.trim(),
      };

      await serviceRegisterOrganization(payload);

      toast.success("Organization created successfully!");
      setTimeout(() => navigate("/createAdmin"), 700);
    } catch (error) {
      // toast.error(`An unexpected error occurred: ${error.message}`);
      // if (response.status === 400) {
      //   toast.error("Invalid organization data or code already exists.");
      // } else {
      //   const msg = await response.text().catch(() => "");
      //   toast.error(`Failed to create organization.${msg ? ` ${msg}` : ""}`);
      // }

      if (error?.status === 400) {
        toast.error(
          error?.message ||
            "Invalid organization data or organization code already exists.",
        );
      } else if (error?.status === 409) {
        toast.error(
          error?.message || "An organization with this code already exists.",
        );
      } else {
        toast.error(
          error?.message ||
            "An unexpected error occurred while creating the organization.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="org-page">
      <header className="org-header">
        <button
          type="button"
          className="org-btn-back"
          onClick={() => navigate("/")}
          disabled={loading}
          title="Back"
        >
          <i className="bi bi-chevron-left" aria-hidden="true" />
          Back
        </button>

        <div className="org-header-right">
          <div className="org-header-title">
            <div className="org-header-title-main">Fleet Administration</div>
            <div className="org-header-title-sub">Setup New Organisation</div>
          </div>
          <img src={logoFM} alt="Fleet Master" className="org-logo" />
        </div>
      </header>

      <main className="org-main">
        <div className="org-card">
          <div className="org-card-topbar">
            <div className="org-chip">
              <i className="bi bi-building-plus" aria-hidden="true" />
              Organization setup
            </div>

            {loading ? (
              <div className="org-status">
                <span className="org-spinner" aria-hidden="true" />
                Creating…
              </div>
            ) : (
              <div className="org-status is-idle">
                <i className="bi bi-check2" aria-hidden="true" />
                Ready
              </div>
            )}
          </div>

          <form
            className="org-form"
            onSubmit={handleSubmit}
            id="createOrganization"
          >
            <div className="org-form-head">
              <div className="org-form-title">Register an Organization</div>

              <div className="org-form-sub">
                Initialize a new entity workspace inside Fleet Master.
              </div>
            </div>

            <div className="org-grid">
              <div className="org-field">
                <label className="org-label" htmlFor="orgName">
                  Organization Name
                </label>

                <input
                  id="orgName"
                  type="text"
                  className="org-input"
                  placeholder="e.g. Logistics Corp"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  disabled={loading}
                  autoComplete="organization"
                  required
                />
              </div>

              <div className="org-field">
                <label className="org-label" htmlFor="orgCode">
                  Organization Code
                </label>

                <input
                  id="orgCode"
                  type="text"
                  className="org-input"
                  placeholder="e.g. TRUK"
                  value={orgCode}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/[^a-zA-Z]/g, "")
                      .slice(0, 4)
                      .toUpperCase();

                    setOrgCode(value);
                  }}
                  maxLength={4}
                  disabled={loading}
                  required
                />

                <div className="org-hint">
                  Enter a unique four-letter organization code.
                </div>
              </div>

              <div className="org-field">
                <label className="org-label" htmlFor="orgAddress">
                  Organization Address
                </label>

                <input
                  id="orgAddress"
                  type="text"
                  className="org-input"
                  placeholder="e.g. Sydney, Australia"
                  value={orgAddress}
                  onChange={(e) => setOrgAddress(e.target.value)}
                  disabled={loading}
                  autoComplete="street-address"
                />
              </div>
            </div>

            <div className="org-actions">
              <button
                className="org-btn org-btn-primary"
                type="submit"
                disabled={!canSubmit}
                title={
                  !canSubmit
                    ? "Enter an organization name and four-letter code"
                    : undefined
                }
              >
                <i className="bi bi-plus-circle" aria-hidden="true" />

                <span>{loading ? "Creating…" : "Create organization"}</span>
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default CreateOrganization;
